import { resolveCurrencySymbol } from './constants'
import type { Activity } from '@/types/resources/activity.ts'

/**
 * Compute activity tooltip for display based on activity type and data
 * @param activity Activity object from server
 * @returns Plain string for activity tooltip
 */
export function computeActivityTooltip(activity: Activity, currencySymbol: string = '$'): string {
  if (!activity) return ''

  const info = activity.additionalInfo || {}
  const fanName = getFanName(activity)

  const activityTypeMap = {
    'fan-message': () => getFanMessageTitle(info),
    'data-capture': () => getCampaignTitle(info, 'Signed up'),
    'campaign-registration': () => getCampaignTitle(info, 'Signed up'),
    'short-link': () => 'Clicked on short link ' + (info.linkName || 'short link'),
    'link-activity': () => 'Clicked on short link ' + (info.linkName || 'short link'),
    'short-link-click': () => getShortLinkClickTitle(info),
    'website-activity': () => `Visited website ${info.websiteName || 'website'}`,
    'subscription': () => getSubscriptionTitle(info),
    'event-attendance': () => getEventAttendanceTitle(info, fanName),
    'event-purchase': () => getPurchaseTitle(info, currencySymbol),
    'ecommerce-purchase': () => getPurchaseTitle(info, currencySymbol),
    'automation': () => getAutomationTitle(info),
    'profile-details': () => getProfileDetailsTitle(info),
    'default': () => activity.activityType || 'Activity',
  }

  return (activityTypeMap[activity.activityType] || activityTypeMap.default)()
}

function getFanName(activity: Activity): string {
  const { fanPromoterAccount } = activity
  if (fanPromoterAccount && (fanPromoterAccount.firstName || fanPromoterAccount.lastName)) {
    return `${fanPromoterAccount.firstName || ''} ${fanPromoterAccount.lastName || ''}`.trim()
  }
  return 'Contact'
}

function getFanMessageTitle(info: any): string {
  const provider = info.provider === 'email' ? 'Email' : 'SMS'
  const campaignName = info.campaignCustomerName || 'Message'
  const statusMap = {
    sent: 'Sent',
    clicked: 'Received',
    opened: 'Received',
    received: 'Received',
    bounced: 'Bounced',
    failed: 'Failed',
  }
  const status = statusMap[info.status] || ''
  const text = status ? `${status} ${provider}:` : campaignName
  const automationSuffix = info.automationChartName ? ' — via Automation' : ''
  return `${text} ${campaignName}${automationSuffix}`
}

function getCampaignTitle(info: any, signUpType: string): string {
  const campaignName = info.campaignName || 'Campaign'
  const captureType = info.captureType?.toLowerCase() || ''
  if (captureType.includes('presale')) signUpType += ' Presale access'
  else if (captureType.includes('waitlist')) signUpType += ' Waitlist'
  return `${signUpType}: ${campaignName}`
}

function getShortLinkClickTitle(info: any): string {
  const targetUrl = info.targetUrl || info.shortUrl || 'link'
  const clickCount = info.clickCount || 1
  return `Clicked short link ${targetUrl} (${clickCount} ${clickCount === 1 ? 'click' : 'clicks'})`
}

function getSubscriptionTitle(info: any): string {
  const status = info.status === 'subscribed' ? 'Subscribed to' : 'Unsubscribed from'
  const channelMap = {
    email: 'Email',
    sms: 'SMS',
  }
  const channels = info.actions?.[0]?.channels || []
  const channelText = channels.map(channel => channelMap[channel]).join(' & ') || 'Email & SMS'
  const listName = info.messageBody || 'subscription list'
  const sourceMap = {
    'preference-center': ' — via Preference center',
    'automation': ' — via Automation',
    'system': ' — via manual update',
  }
  const source = sourceMap[info.provider] || (info.campaignName ? ` — via ${info.campaignName}` : '')
  return `${status} ${channelText} ${listName}${source}`
}

function getEventAttendanceTitle(info: any, fanName: string): string {
  const eventName = info.eventName || 'Event'
  return `${fanName} attended ${eventName}`
}

function getPurchaseTitle(info: any, currencySymbol: string): string {
  // As in computeActivityTitle: the value's own currency wins.
  const symbol = resolveCurrencySymbol(info.currencyCode, currencySymbol)
  const { quantity = 1, eventName = 'Event', ticketType = 'General Admission', totalCost, totalAmount } = info
  const amount = totalCost || totalAmount
  const cost = typeof amount === 'number' ? ` — ${symbol}${(amount / 100).toFixed(2)}` : ''
  const statusMap = {
    cancelled: 'Cancelled',
    refunded: 'Refunded',
  }
  const status = statusMap[info.status] || 'Purchased'
  return `${status} ${quantity}x ${ticketType} tickets to ${eventName}${cost}`
}

function getAutomationTitle(info: any): string {
  const { automationName, automationChartName, actionType, messageBody, listName } = info
  const automationTitle = automationName || automationChartName || 'Automation'
  const actionMap = {
    email: `Sent Email: ${messageBody || 'Email'} — via Automation`,
    subscription: `Subscribed to ${listName || 'subscription list'} — via Automation`,
    tag: 'Tags added',
    enrolled: `Enrolled in Automation: ${automationTitle}`,
  }
  return actionMap[actionType] || `Automation: ${automationTitle}`
}

function getProfileDetailsTitle(info: any): string {
  const { fieldName = 'Field', oldValue, newValue, source } = info
  if (fieldName.toLowerCase().includes('tag')) {
    if (oldValue && !newValue) return 'Tags removed'
    if (!oldValue && newValue) return 'Tags added'
    return 'Tags updated'
  }
  const sourceMap = {
    csv: ' — via csv import',
    manual: ' — via manual update',
  }
  const sourceText = sourceMap[source] || ''
  return `${fieldName} updated to "${newValue}"${sourceText}`
}
